import type { ChangeEvent, FC, FormEvent, ReactElement } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { useUITranslation } from '../../../services/uiTranslations/reactBindings/hooks';
import { L10nText } from '../../../services/uiTranslations/reactBindings/L10nText';
import { useModifiers } from '../../../styles/hooks/useModifiers';
import { Icon } from '../../icons/icon';
import { Icons } from '../../icons/iconNames';
import { InputButton } from '../../inputButton/InputButton';
import { InputGroup } from '../../inputGroup/InputGroup';
import { BaseInput } from '../../inputs/baseInput/BaseInput';
import { MiniChatInput } from '../miniChatInput/MiniChatInput';

import { SupportingText } from './SupportingText';

import styles from './MessageInput.module.css';

interface MessageInputProps {
  isBetRequiredToChat: boolean;
  isGameChat: boolean;
  isGameChatBlocked: boolean;
  isGameChatUnavailable: boolean;
  isLiveSupportChatBlocked: boolean;
  onSubmit: (input: string) => void;
  cashierCallback?: () => void;
  characterLimit?: number;
  isMiniChat?: boolean;
}

export const MessageInput: FC<MessageInputProps> = ({
  cashierCallback,
  characterLimit = 200,
  isBetRequiredToChat,
  isGameChat,
  isGameChatBlocked,
  isGameChatUnavailable,
  isLiveSupportChatBlocked,
  isMiniChat,
  onSubmit,
}): ReactElement => {
  const [gameChatInputValue, setGameChatInputValue] = useState(''); // todo: consider moving input value to redux
  const [supportChatInputValue, setSupportChatInputValue] = useState(''); // todo: consider moving input value to redux

  const [inputFocused, setInputFocused] = useState(false);

  const chatInputRef = useRef<HTMLInputElement>(null);
  const triggeredMouseEvent = useRef<boolean>(false);

  const chatIsBlockedTest = useUITranslation('CHAT_IS_BLOCKED');
  const typeMessageText = useUITranslation('TYPE_MESSAGE');

  const isLiveSupportChatTabSelected = !isMiniChat && !isGameChat;
  const inputValue = isGameChat ? gameChatInputValue : supportChatInputValue;
  const setInputValue = isGameChat ? setGameChatInputValue : setSupportChatInputValue;

  const isOverLimit = useMemo(() => inputValue.length === characterLimit, [inputValue, characterLimit]);

  const isDisabled = useMemo(() => {
    if (isGameChatBlocked && (isGameChat || isMiniChat)) {
      return true;
    }

    return isLiveSupportChatBlocked && isLiveSupportChatTabSelected && !isMiniChat;
  }, [isGameChatBlocked, isGameChat, isLiveSupportChatBlocked, isLiveSupportChatTabSelected, isMiniChat]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value.slice(0, characterLimit);
      setInputValue(value);
    },
    [characterLimit, setInputValue]
  );

  // Handling chatunaviable messages when click on disabled button
  const handleMouseDownEvent = useCallback(() => {
    if (isGameChatUnavailable && !isLiveSupportChatTabSelected && !triggeredMouseEvent.current) {
      triggeredMouseEvent.current = true;
    }
  }, [isGameChatUnavailable, isLiveSupportChatTabSelected]);

  const handleInputFocus = useCallback(
    (inputFocused: boolean) => {
      if (isGameChatUnavailable && !isLiveSupportChatTabSelected) {
        if (triggeredMouseEvent.current && !inputFocused) {
          setInputFocused(true);
        } else {
          setInputFocused(inputFocused);
        }
        triggeredMouseEvent.current = false;
      }
    },
    [isGameChatUnavailable, isLiveSupportChatTabSelected, setInputFocused]
  );

  const handleSendMessage = useCallback(() => {
    // ignoring send message in  bet requirement and insufficient funds messages cases
    if (isGameChatUnavailable && !isLiveSupportChatTabSelected) {
      chatInputRef.current?.focus();
      setInputFocused(true);
      return;
    }
    // Prevent from sending an empty message
    if (inputValue.trim()) {
      onSubmit(inputValue);
      setInputValue('');
    }
  }, [inputValue, isGameChatUnavailable, isLiveSupportChatTabSelected, onSubmit, setInputValue]);

  const handleFormSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      handleSendMessage();
    },
    [handleSendMessage]
  );

  const getPlaceholder = useCallback((): string | undefined => {
    if (isGameChatBlocked && (isGameChat || isMiniChat)) {
      return chatIsBlockedTest;
    }

    if (isLiveSupportChatBlocked && isLiveSupportChatTabSelected && !isMiniChat) {
      return chatIsBlockedTest;
    }

    return typeMessageText;
  }, [
    isGameChat,
    isGameChatBlocked,
    isLiveSupportChatBlocked,
    isLiveSupportChatTabSelected,
    isMiniChat,
    chatIsBlockedTest,
    typeMessageText,
  ]);

  const shouldShowSupportMessage = useMemo(
    () => !isGameChatBlocked && isGameChatUnavailable && (isGameChat || isMiniChat) && inputFocused,
    [isGameChatBlocked, isGameChatUnavailable, isGameChat, isMiniChat, inputFocused]
  );

  const chatUnavaiableOptions = useMemo(() => {
    if (!isLiveSupportChatTabSelected && isGameChatUnavailable) {
      return {
        fill: `var(--core-color-button-disable-base)`,
      };
    }
    return {};
  }, [isLiveSupportChatTabSelected, isGameChatUnavailable]);

  const mods = useModifiers(
    styles,
    'messageInput',
    {
      miniChat: isMiniChat,
      disabled: isDisabled,
    },
    { withBaseClass: true }
  );

  return (
    <form className={mods} onSubmit={handleFormSubmit} id="chat-form" autoComplete="off">
      {isMiniChat ? (
        <MiniChatInput
          inputRef={chatInputRef}
          disabled={isDisabled}
          errorMessage={isOverLimit && !isGameChatUnavailable ? <L10nText tk="CHAT_MAX" /> : null}
          isGameChatUnavailable={isGameChatUnavailable}
          maxLength={characterLimit}
          name="chat-message-input"
          onBlur={() => handleInputFocus(false)}
          onChange={handleChange}
          onFocus={() => handleInputFocus(true)}
          placeholder={getPlaceholder()}
          handleMouseDownEvent={handleMouseDownEvent}
          supportingText={
            shouldShowSupportMessage ? (
              <SupportingText isBetRequiredToChat={isBetRequiredToChat} cashierCallback={cashierCallback} />
            ) : null
          }
          value={inputValue}
        />
      ) : (
        <InputGroup
          input={
            <BaseInput
              inputRef={chatInputRef}
              disabled={isDisabled}
              errorMessage={
                isOverLimit && !(!isLiveSupportChatTabSelected && isGameChatUnavailable) ? (
                  <L10nText tk="CHAT_MAX" />
                ) : null
              }
              maxLength={characterLimit}
              name="chat-message-input"
              onBlur={() => handleInputFocus(false)}
              onChange={handleChange}
              onFocus={() => handleInputFocus(true)}
              placeholder={getPlaceholder()}
              supportingText={
                shouldShowSupportMessage ? (
                  <SupportingText isBetRequiredToChat={isBetRequiredToChat} cashierCallback={cashierCallback} />
                ) : null
              }
              value={inputValue}
              containerClass={styles.messageInput__inputContainer}
            />
          }
        >
          {isDisabled ? null : (
            <InputButton
              disabled={!isLiveSupportChatTabSelected && isGameChatUnavailable ? false : !inputValue}
              icon={<Icon {...chatUnavaiableOptions} icon={Icons.Send} />}
              onClick={handleSendMessage}
              onMouseDown={handleMouseDownEvent}
            />
          )}
        </InputGroup>
      )}
    </form>
  );
};
